import { Capacitor } from '@capacitor/core';

import { config, isNative } from './config';

/*
 * The one-time "lifetime" unlock, bought through the App Store / Google Play (cordova-plugin-purchase,
 * a non-consumable product). There is no server and no account: the store remembers the purchase,
 * and "Restore" asks it again after a reinstall or on a new device. The plugin object is a global,
 * read only here so the rest of the app stays plain data and testable without a device.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cdv = (): any => (window as any).CdvPurchase;

const platformOf = () =>
    Capacitor.getPlatform() === 'ios'
        ? cdv().Platform.APPLE_APPSTORE
        : cdv().Platform.GOOGLE_PLAY;

export const lifetimeSupported = (): boolean => isNative();

const whenReady = (): Promise<void> =>
    new Promise((resolve) => {
        if (cdv()) return resolve();
        document.addEventListener('deviceready', () => resolve(), {
            once: true,
        });
    });

export interface LifetimeListener {
    onOwned: () => void;
    onError: (message: string) => void;
    onPrice: (price: string) => void;
}

let starting: Promise<void> | null = null;

/** Connects to the store, registers the product, and reports ownership and price. Safe to call twice. */
export const initLifetime = (listener: LifetimeListener): Promise<void> => {
    if (!isNative()) return Promise.resolve();
    if (starting) return starting.then(() => refresh(listener));

    starting = (async () => {
        try {
            await whenReady();
            const { store, ProductType } = cdv();
            const platform = platformOf();
            store.register([
                {
                    id: config.lifetimeProductId,
                    type: ProductType.NON_CONSUMABLE,
                    platform,
                },
            ]);

            // Nothing to validate on a server: acknowledge the purchase so the store does not refund it.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            store.when().approved((transaction: any) => transaction.finish());
            store.when().finished(() => refresh(listener));
            store.when().productUpdated(() => refresh(listener));
            store.error((e: { code?: number; message?: string }) => {
                // 6777006 is the user cancelling the purchase sheet; not worth showing.
                if (e.code !== 6777006)
                    listener.onError(
                        e.message ?? 'The store reported a problem.'
                    );
            });

            await store.initialize([platform]);
            await store.update();
            refresh(listener);
        } catch (error) {
            starting = null;
            listener.onError(
                (error as Error)?.message ?? 'The store is not available.'
            );
        }
    })();
    return starting;
};

const refresh = (listener: LifetimeListener) => {
    const api = cdv();
    if (!api) return;
    const product = api.store.get(config.lifetimeProductId, platformOf());
    const price = product?.getOffer?.()?.pricingPhases?.[0]?.price;
    if (price) listener.onPrice(price);
    if (api.store.owned(config.lifetimeProductId)) listener.onOwned();
};

/** Opens the store's purchase sheet. Ownership arrives through the listener once the store confirms. */
export const buyLifetime = async (): Promise<string | undefined> => {
    const api = cdv();
    const offer = api?.store
        .get(config.lifetimeProductId, platformOf())
        ?.getOffer?.();
    if (!offer) return 'The store is not ready yet. Try again in a moment.';
    const failure = await offer.order();
    return failure && failure.code !== 6777006 ? failure.message : undefined;
};

/** Asks the store for past purchases; owned ones arrive through the listener. */
export const restoreLifetime = async (): Promise<string | undefined> => {
    const api = cdv();
    if (!api) return 'The store is not available here.';
    const failure = await api.store.restorePurchases();
    return failure?.message;
};
